// NPM Imports
import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Route, Switch, useLocation } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { Provider } from "react-redux";
import * as Sentry from "@sentry/browser";

// Components Imports
import { ErrorBoundaryFallback, LazyLoad } from "../commonComponents";
import { AppUpdateNotification, NavBarWrapper } from "../components";
import PublicRoute from "./PublicRoute";
import PrivateRoute from "./PrivateRoute";
import { GlobalUtils } from "../utils";

// Internal Imports
import store from "../store";
import { pageViewEvent } from "../gtm/gtmutils";

// Depedency Imports
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min";
import "react-datepicker/dist/react-datepicker.css";
import "./index.css";
import LZLoader from "../commonComponents/LZLoader";

// Page Imports
const Asset = LazyLoad(() =>
  import(/* webpackChunkName: "asset" */ "../pages/Asset"),
);

// const Assets = LazyLoad(() =>
//   import(/* webpackChunkName: "assets" */ "../pages/Assets"),
// );

// const Dashboard = LazyLoad(() =>
//   import(/* webpackChunkName: "dashboard" */ "../pages/Dashboard"),
// );

const DocumentTemplate = LazyLoad(() =>
  import(
    /* webpackChunkName: "documenttemplate" */ "../pages/DocumentTemplate"
  ),
);

const StaticTemplateActions = LazyLoad(() =>
  import(
    /* webpackChunkName: "statictemplateactions" */ "../pages/StaticTemplateActions"
  ),
);

const FourOhFour = LazyLoad(() =>
  import(/* webpackChunkName: "documenttemplate" */ "../pages/FourOhFour"),
);

const LandingPage = LazyLoad(() =>
  import(/* webpackChunkName: "documenttemplate" */ "../pages/LandingPage"),
);

const Inbox = LazyLoad(() =>
  import(/* webpackChunkName: "inbox" */ "../pages/Inbox"),
);

const Pipeline = LazyLoad(() =>
  import(/* webpackChunkName: "pipeline" */ "../pages/PipelineV2"),
);

const Pricebooks = LazyLoad(() =>
  import(/* webpackChunkName: "pricebooks" */ "../pages/Pricebooks"),
);

const Products = LazyLoad(() =>
  import(/* webpackChunkName: "products" */ "../pages/Products"),
);

const Quotations = LazyLoad(() =>
  import(/* webpackChunkName: "quotations" */ "../pages/Quotations"),
);

const TransactionalPlanRedirectCallback = LazyLoad(() =>
  import(
    /* webpackChunkName: "quotations" */ "../commonComponents/TransactionalPlanPopup/TransactionalPlanRedirect"
  ),
);

const SFSAMLAuth = LazyLoad(() =>
  import(/* webpackChunkName: "samlauthentication" */ "../pages/SFSAMLAuth"),
);

const GoogleSheetAuth = LazyLoad(() =>
  import(
    /* webpackChunkName: "samlauthentication" */ "../pages/GoogleSheetAuth"
  ),
);

const Settings = LazyLoad(() =>
  import(/* webpackChunkName: "settings" */ "../pages/Settings"),
);

const Templates = LazyLoad(() =>
  import(/* webpackChunkName: "templates" */ "../pages/Templates"),
);

// const FieldsSetting = LazyLoad(() =>
//   import(/* webpackChunkName: "templates" */ "../pages/FieldsSetting"),
// );

const DataSourceCallback = LazyLoad(() =>
  import(/* webpackChunkName: "templates" */ "../pages/DataSourceCallback"),
);

const WorkFlow = LazyLoad(() =>
  import(/* webpackChunkName: "workflow" */ "../pages/WorkFlow"),
);

const SharedDocument = LazyLoad(() =>
  import(/* webpackChunkName: "shareddocument" */ "../pages/SharedDocument"),
);

const SharedDocumentPerma = LazyLoad(() =>
  import(
    /* webpackChunkName: "shareddocument" */ "../pages/SharedDocumentPerma"
  ),
);

const SharedMasterDocument = LazyLoad(() =>
  import(
    /* webpackChunkName: "shareddocument" */ "../pages/SharedMasterDocument"
  ),
);

const Sheet = LazyLoad(() =>
  import(/* webpackChunkName: "shareddocument" */ "../pages/Sheet"),
);

const OauthConsent = LazyLoad(() =>
  import(/* webpackChunkName: "shareddocument" */ "../pages/OauthConsent"),
);

const NotificationBanner = LazyLoad(() =>
  import(
    /* webpackChunkName: "NotificationBanner" */ "../pages/NotificationBanner"
  ),
);

const RevvSign = LazyLoad(() =>
  import(/* webpackChunkName: "RevvSign" */ "../pages/RevvSign"),
);

// Function to handle lazyloading
const LazyLoadingComponent = Component => props =>
  (
    <Suspense fallback={<LZLoader label="Please wait..." />}>
      <Component {...props} />
    </Suspense>
  );

const boundryErrorHandler = (error: Error, info) => {
  let extraInfo = "";
  if (
    info &&
    info.componentStack &&
    (typeof info.componentStack === "string" ||
      info.componentStack instanceof String)
  ) {
    extraInfo = info.componentStack;
  }

  Sentry.configureScope(scope => {
    const CURRENT_USER = GlobalUtils.readDataFromLocalStorage("revvsalesUser");
    scope.setLevel("critical");
    scope.setTag("custom_tag", "Router Boundry Error");
    scope.setExtra("app_version", process.env.REACT_APP_VERSION);
    scope.setExtra("payload", JSON.stringify(error));
    scope.setExtra(
      "user-email",
      (CURRENT_USER && CURRENT_USER.user_email) || "",
    );
    if (extraInfo) {
      scope.setExtra("info", info);
    }
    Sentry.captureException(error);
  });
};

const AppWrapper = () => {
  const location = useLocation();

  useEffect(() => {
    pageViewEvent(location.pathname);
  }, [location]);

  return (
    <Provider store={store}>
      <div className="app">
        <ErrorBoundary
          FallbackComponent={ErrorBoundaryFallback}
          onError={boundryErrorHandler}>
          <Route path="" component={NavBarWrapper} />
          <Route path="" component={AppUpdateNotification} />
          {/* NOTE: use NotificationBanner component to show all top banner related contents */}
          <Route path="" component={LazyLoadingComponent(NotificationBanner)} />
          <Switch>
            {/* OTHER ROUTES */}
            <PublicRoute
              path="/shared/documents/:docID/:sharingHash"
              component={LazyLoadingComponent(SharedDocument)}
            />
            <PublicRoute
              path="/perma/DOC/:sharingHash/:docName"
              component={LazyLoadingComponent(SharedDocumentPerma)} //* Permalink old url
            />
            <PublicRoute
              path="/m/:newPermalinkUrl"
              component={LazyLoadingComponent(SharedDocumentPerma)} //* Permalink new url
            />
            <PublicRoute
              path="/f/:newMasterDocUrl"
              component={LazyLoadingComponent(SharedMasterDocument)}
            />
            <PublicRoute
              path="/signing-documents/:docID/:signingHash"
              component={LazyLoadingComponent(RevvSign)}
            />
            <PrivateRoute
              exact
              path="/products"
              component={LazyLoadingComponent(Products)}
            />
            <PrivateRoute
              path="/pricebooks/:pricebookId?"
              component={LazyLoadingComponent(Pricebooks)}
            />
            <PrivateRoute
              exact
              path={["/assets/:folderName?/:folderID?", "/sf/pipeline"]}
              component={LazyLoadingComponent(Asset)}
            />
            {/* <PrivateRoute
            path="/old-assets/:accountID?"
            component={LazyLoadingComponent(Assets)}
          /> */}
            {/**
            <PrivateRoute
            path="/dashboard"
            component={LazyLoadingComponent(Dashboard)}
          />
           */}
            <PrivateRoute
              path="/inbox"
              component={LazyLoadingComponent(Inbox)}
            />
            <PrivateRoute
              path="/documents/:docNo"
              component={LazyLoadingComponent(Quotations)}
            />
            <PrivateRoute
              exact
              path="/pipeline"
              component={LazyLoadingComponent(Pipeline)}
            />
            <PrivateRoute
              path="/templates/:templateView?"
              component={LazyLoadingComponent(Templates)}
            />
            {/* <PrivateRoute
              path="/fields/:fieldView/:fieldDetailView?"
              component={LazyLoadingComponent(FieldsSetting)}
            /> */}
            <PrivateRoute
              path="/datastudio/:datasource/:callback?"
              component={LazyLoadingComponent(DataSourceCallback)}
            />
            <PrivateRoute
              path="/transactionplan/:status"
              component={LazyLoadingComponent(
                TransactionalPlanRedirectCallback,
              )}
            />
            <PrivateRoute
              path="/workflow/:workflowId?"
              component={LazyLoadingComponent(WorkFlow)}
            />
            <PrivateRoute
              path="/template/:templateID"
              component={LazyLoadingComponent(DocumentTemplate)}
            />
            <PrivateRoute
              path="/preview/templates/:globalTemplate"
              component={LazyLoadingComponent(StaticTemplateActions)}
            />
            <PrivateRoute
              path="/settings/:settingsView/:detailView?"
              component={LazyLoadingComponent(Settings)}
            />
            <PrivateRoute
              path="/sheet"
              component={LazyLoadingComponent(Sheet)}
            />
            <PrivateRoute
              path="/oauth"
              component={LazyLoadingComponent(OauthConsent)}
            />
            <Route
              path="/search"
              component={LazyLoadingComponent(SFSAMLAuth)}
            />
            <Route
              path="/google"
              component={LazyLoadingComponent(GoogleSheetAuth)}
            />
            <PrivateRoute
              path="/"
              component={LazyLoadingComponent(LandingPage)}
            />
            <Route component={LazyLoadingComponent(FourOhFour)} />
          </Switch>
        </ErrorBoundary>
      </div>
    </Provider>
  );
};

const App = () => (
  <BrowserRouter>
    <AppWrapper />
  </BrowserRouter>
);

export default App;
